import { useState } from 'react';
import { useEvents } from '../hooks/useEvents';
import { nthWeekdayLabel, dayOfMonthLabel } from './EventEditorSheet';
import EventRow, { eventEditPayload } from './EventRow';
import EventDetailSheet from './EventDetailSheet';

// AllEventsSheet — "See all events" (docs/designs/AllEvents.png), opened
// directly from This Week (a page), so this is a plain full-screen sheet of
// its own — not a <StackedSheet> (that's reserved for things opened from
// INSIDE a sheet; EventDetailSheet, opened from inside this one, is the
// StackedSheet here instead).
//
// "This week" first, then one section per month for the next 12 months — no
// filter chips. Recurring events are deduped to their next occurrence only
// (the hook returns every expansion in the window; this just keeps the
// first — earliest — one per event id, since the list is already sorted).
//
// Props: open, onClose, churchId, isModerator, onAddEvent(), onEditEvent(payload, occurrenceDate)

const SANS = "'Plus Jakarta Sans', system-ui, sans-serif";

function recurrenceLabelFor(ev) {
  switch (ev.recurring) {
    case 'weekly':         return 'Every week';
    case 'fortnightly':    return 'Every 2 weeks';
    case 'monthly_date':   return `Monthly on ${dayOfMonthLabel(ev.occurrenceDate)}`;
    case 'monthly_weekday': return `Monthly on ${nthWeekdayLabel(ev.occurrenceDate)}`;
    default: return null;
  }
}

function monthLabel(date, now) {
  const label = date.toLocaleDateString('en-GB', { month: 'long' }).toUpperCase();
  return date.getFullYear() !== now.getFullYear() ? `${label} ${date.getFullYear()}` : label;
}

export default function AllEventsSheet({ open, onClose, churchId, isModerator, onAddEvent, onEditEvent }) {
  const { events, toggleGoing } = useEvents(churchId, { monthsAhead: 12, enabled: open });
  const [rsvpError, setRsvpError] = useState(null);
  const [detailEv, setDetailEv] = useState(null);

  if (!open) return null;

  async function handleToggleGoing(ev) {
    const key = `${ev.id}-${ev.occurrenceDate}`;
    setRsvpError(null);
    const { error } = await toggleGoing(ev.id, ev.occurrenceDate, ev.going);
    if (error) setRsvpError({ key, message: error });
  }

  // Keep only the earliest occurrence per event id — `events` is already
  // sorted by occursAt, so the first one seen per id is the next one.
  const seen = new Set();
  const deduped = events.filter(ev => {
    if (seen.has(ev.id)) return false;
    seen.add(ev.id);
    return true;
  });

  const now = new Date(); now.setHours(0, 0, 0, 0);
  const weekAhead = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  const thisWeek = deduped.filter(ev => ev.occursAt < weekAhead);
  const later = deduped.filter(ev => ev.occursAt >= weekAhead);

  const monthSections = [];
  for (const ev of later) {
    const key = `${ev.occursAt.getFullYear()}-${ev.occursAt.getMonth()}`;
    let section = monthSections.find(s => s.key === key);
    if (!section) { section = { key, label: monthLabel(ev.occursAt, now), events: [] }; monthSections.push(section); }
    section.events.push(ev);
  }

  function openEdit(ev) { onEditEvent?.(eventEditPayload(ev), ev.occurrenceDate); }

  return (
    <div style={S.backdrop} onClick={e => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div style={S.sheet}>
        <div style={S.header}>
          <button onClick={onClose} aria-label="Back" style={S.iconBtn}>‹</button>
          <div style={{ flex: 1, fontSize: 16, fontWeight: 800, color: 'var(--ink)', textAlign: 'center' }}>All events</div>
          {isModerator ? (
            <button onClick={onAddEvent} aria-label="Add event" style={S.iconBtn}>＋</button>
          ) : (
            <div style={{ width: 34 }}/>
          )}
        </div>

        <div style={S.scroll}>
          {deduped.length === 0 ? (
            <div style={S.empty}>Nothing on the calendar yet.</div>
          ) : (
            <>
              {thisWeek.length > 0 && (
                <>
                  <div style={S.sectionLabel}>This week</div>
                  {thisWeek.map(ev => (
                    <EventRow key={`${ev.id}-${ev.occurrenceDate}`} ev={ev}
                      onToggleGoing={handleToggleGoing} rsvpError={rsvpError}
                      onClick={setDetailEv} recurrenceLabel={recurrenceLabelFor(ev)}
                    />
                  ))}
                </>
              )}
              {monthSections.map(section => (
                <div key={section.key}>
                  <div style={S.sectionLabel}>{section.label}</div>
                  {section.events.map(ev => (
                    <EventRow key={`${ev.id}-${ev.occurrenceDate}`} ev={ev}
                      onToggleGoing={handleToggleGoing} rsvpError={rsvpError}
                      onClick={setDetailEv} recurrenceLabel={recurrenceLabelFor(ev)}
                    />
                  ))}
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      <EventDetailSheet open={!!detailEv} onClose={() => setDetailEv(null)} ev={detailEv}
        isModerator={isModerator} onToggleGoing={handleToggleGoing} rsvpError={rsvpError}
        onEdit={ev => { setDetailEv(null); openEdit(ev); }}
      />
    </div>
  );
}

const S = {
  backdrop: { position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.6)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', fontFamily: SANS },
  sheet: { background: 'var(--cream)', width: '100%', maxWidth: 480, margin: '0 auto', height: '100%', display: 'flex', flexDirection: 'column', borderLeft: '1px solid var(--border)', borderRight: '1px solid var(--border)' },
  header: { flexShrink: 0, display: 'flex', alignItems: 'center', padding: '14px 16px', borderBottom: '1px solid var(--border)', minHeight: 54, background: 'var(--white)' },
  iconBtn: { background: 'rgba(31,27,22,0.06)', border: 'none', color: 'var(--stone)', width: 34, height: 34, borderRadius: 999, cursor: 'pointer', fontSize: 17, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  scroll: { flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '16px 16px calc(env(safe-area-inset-bottom) + 20px)' },
  empty: { fontSize: 13.5, color: 'var(--stone)', padding: '30px 10px', textAlign: 'center' },
  sectionLabel: { fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--stone)', margin: '18px 2px 10px' },
};
